import { ApiResponse, CreateProjectRequest, ProjectRow, CapitalProject, SetCapitalProjectRequest } from '../../lib/types';
import { getApiUrl } from '../lib/api-config';

// Helper to add auth token to requests
function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

export async function createProject(req: CreateProjectRequest): Promise<ApiResponse<ProjectRow>> {
  const response = await fetch(getApiUrl('/projects'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function getProjects(status?: string): Promise<ApiResponse<ProjectRow[]>> {
  const url = getApiUrl(`/projects${status ? `?status=${status}` : ''}`);
  console.log('Fetching projects from:', url);
  const response = await fetch(url, { headers: getAuthHeaders() });
  if (!response.ok) {
    console.error('Projects fetch failed:', response.status, response.statusText);
    const text = await response.text();
    console.error('Response:', text.substring(0, 200));
  }
  return response.json();
}

export async function getProject(id: string): Promise<ApiResponse<ProjectRow>> {
  const response = await fetch(getApiUrl(`/projects/${id}`));
  return response.json();
}

export async function submitGovernanceAssessment(req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/governance/assess'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function submitGateDecision(req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/governance/gate-decision'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function submitHealthReport(req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/health-reports'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function getHealthReports(): Promise<ApiResponse<any[]>> {
  const response = await fetch(getApiUrl('/health-reports'));
  return response.json();
}

export async function getHealthReportsByProject(projectId: string): Promise<ApiResponse<any[]>> {
  const response = await fetch(getApiUrl(`/health-reports/${projectId}`));
  return response.json();
}

// Timesheet API functions
export interface PendingTimesheet {
  TimesheetId: string;
  UserId: string;
  UserName: string;
  UserEmail: string;
  WeekStartDate: string;
  SubmittedDate: string;
  Status: string;
  TotalHours?: number;
  TeamId?: string;
}

export interface TimesheetEntryDetail {
  TimesheetEntryId: string;
  TimesheetId: string;
  DayOfWeek: string;
  HoursLogged: number;
  ProjectId?: string;
  GLCodeId?: string;
  ProjectTitle?: string;
  GLCodeName?: string;
  Notes?: string;
}

export interface TimesheetStats {
  PendingCount: number;
  ApprovedThisWeekCount: number;
  TeamMemberCount: number;
}

export async function getPendingTimesheets(teamId?: string): Promise<ApiResponse<PendingTimesheet[]>> {
  const url = getApiUrl(`/timesheets/pending${teamId ? `?teamId=${teamId}` : ''}`);
  const response = await fetch(url);
  return response.json();
}

export async function getTimesheetEntries(timesheetId: string): Promise<ApiResponse<TimesheetEntryDetail[]>> {
  const response = await fetch(getApiUrl(`/timesheets/${timesheetId}/entries`));
  return response.json();
}

export async function approveTimesheet(
  timesheetId: string,
  approvedBy: string,
  comments?: string
): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl(`/timesheets/${timesheetId}/approve`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ approvedBy, comments }),
  });
  return response.json();
}

export async function rejectTimesheet(
  timesheetId: string,
  rejectionReason?: string
): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl(`/timesheets/${timesheetId}/reject`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rejectionReason }),
  });
  return response.json();
}

export async function getTimesheetStats(teamId?: string): Promise<ApiResponse<TimesheetStats>> {
  const url = getApiUrl(`/timesheets/stats${teamId ? `?teamId=${teamId}` : ''}`);
  const response = await fetch(url);
  return response.json();
}

// Capital Projects API functions
export async function getCapitalProjects(isCapital?: boolean): Promise<ApiResponse<CapitalProject[]>> {
  const url = getApiUrl(`/capital-projects${isCapital !== undefined ? `?isCapital=${isCapital}` : ''}`);
  const response = await fetch(url);
  return response.json();
}

export async function getCapitalProject(projectId: string): Promise<ApiResponse<CapitalProject>> {
  const response = await fetch(getApiUrl(`/capital-projects/${projectId}`));
  return response.json();
}

export async function setCapitalProject(req: SetCapitalProjectRequest): Promise<ApiResponse<CapitalProject>> {
  const response = await fetch(getApiUrl('/capital-projects'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function updateCapitalProject(projectId: string, req: Partial<SetCapitalProjectRequest>): Promise<ApiResponse<CapitalProject>> {
  const response = await fetch(getApiUrl(`/capital-projects/${projectId}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

// Team Management API Functions
export async function getTeams(): Promise<ApiResponse<any[]>> {
  const response = await fetch(getApiUrl('/teams'));
  return response.json();
}

export async function createTeam(req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/teams'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function updateTeam(teamId: string, req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl(`/teams/${teamId}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function deleteTeam(teamId: string): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl(`/teams/${teamId}`), {
    method: 'DELETE',
  });
  return response.json();
}

// Team Members API Functions
export async function getTeamMembers(teamId?: string): Promise<ApiResponse<any[]>> {
  const url = getApiUrl(`/team-members${teamId ? `?teamId=${teamId}` : ''}`);
  const response = await fetch(url);
  return response.json();
}

export async function createTeamMember(req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/team-members'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function updateTeamMember(memberId: string, req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl(`/team-members/${memberId}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function removeTeamMember(memberId: string): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl(`/team-members/${memberId}`), {
    method: 'DELETE',
  });
  return response.json();
}

// Users API Function
export async function getUsers(role?: string): Promise<ApiResponse<any[]>> {
  const url = getApiUrl(`/users${role ? `?role=${role}` : ''}`);
  const response = await fetch(url);
  return response.json();
}

// Admin Config API Functions

// Departments
export interface Department {
  DepartmentId: string;
  DepartmentName: string;
  DepartmentHead?: string;
  Description?: string;
  Budget?: number;
  IsActive: number;
  CreatedDate: string;
}

export async function getDepartments(): Promise<ApiResponse<Department[]>> {
  const response = await fetch(getApiUrl('/departments'));
  return response.json();
}

export async function createDepartment(req: {
  DepartmentName: string;
  DepartmentHead?: string;
  Description?: string;
  Budget?: number;
}): Promise<ApiResponse<Department>> {
  const response = await fetch(getApiUrl('/departments'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function updateDepartment(
  departmentId: string,
  req: Partial<Department>
): Promise<ApiResponse<Department>> {
  const response = await fetch(getApiUrl(`/departments/${departmentId}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function deleteDepartment(departmentId: string): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl(`/departments/${departmentId}`), {
    method: 'DELETE',
  });
  return response.json();
}

// GL Codes
export interface GLCode {
  GLCodeId: string;
  Code: string;
  Description?: string;
  Category?: string;
  Status?: string;
  IsActive: number;
  CreatedDate: string;
}

export async function getGLCodes(): Promise<ApiResponse<GLCode[]>> {
  const response = await fetch(getApiUrl('/gl-codes'));
  return response.json();
}

export async function createGLCode(req: {
  Code: string;
  Description?: string;
  Category?: string;
  Status?: string;
}): Promise<ApiResponse<GLCode>> {
  const response = await fetch(getApiUrl('/gl-codes'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function updateGLCode(
  glCodeId: string,
  req: Partial<GLCode>
): Promise<ApiResponse<GLCode>> {
  const response = await fetch(getApiUrl(`/gl-codes/${glCodeId}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function deleteGLCode(glCodeId: string): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl(`/gl-codes/${glCodeId}`), {
    method: 'DELETE',
  });
  return response.json();
}

// Project Managers
export interface ProjectManager {
  PMId: string;
  UserId: string;
  ManagerName: string;
  Email: string;
  Department?: string;
  AssignedProjectsCount?: number;
  CreatedDate?: string;
}

export interface ProjectAssignment {
  AssignmentId: string;
  ManagerId: string;
  ProjectId: string;
  CreatedDate?: string;
}

export async function getProjectManagers(): Promise<ApiResponse<ProjectManager[]>> {
  const response = await fetch(getApiUrl('/users?role=PM'));
  return response.json();
}

export async function assignProjectToManager(req: {
  managerId: string;
  projectIds: string[];
}): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/project-managers/assign'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

export async function removeProjectAssignment(assignmentId: string): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl(`/project-managers/${assignmentId}`), {
    method: 'DELETE',
  });
  return response.json();
}

export async function getManagerAssignments(managerId: string): Promise<ApiResponse<ProjectAssignment[]>> {
  const response = await fetch(getApiUrl(`/project-managers/${managerId}/assignments`));
  return response.json();
}

// Health Reports Summary (for dashboards)
export async function getHealthReportsSummary(): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/health-reports/summary'), { headers: getAuthHeaders() });
  return response.json();
}

// Strategic Goals
export async function getStrategicGoals(): Promise<ApiResponse<any[]>> {
  const response = await fetch(getApiUrl('/strategic/goals'), { headers: getAuthHeaders() });
  return response.json();
}

export async function createStrategicGoal(req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/strategic/goals'), {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

// Focus Areas
export async function getFocusAreas(): Promise<ApiResponse<any[]>> {
  const response = await fetch(getApiUrl('/strategic/focus-areas'), { headers: getAuthHeaders() });
  return response.json();
}

export async function createFocusArea(req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/strategic/focus-areas'), {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

// Governance
export async function submitMonthlyReport(req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/reports/monthly'), {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}

// Estimates
export async function createEstimate(req: any): Promise<ApiResponse<any>> {
  const response = await fetch(getApiUrl('/estimates'), {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(req),
  });
  return response.json();
}
